import axios, { AxiosInstance, AxiosResponse } from 'axios'
import toast from 'react-hot-toast'

// Get API URL from environment (same format as main app)
const API_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080'

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    // Get Firebase token from auth context
    const token = localStorage.getItem('firebase_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response
  },
  (error) => {
    // Handle common errors
    if (error.response?.status === 401) {
      // Unauthorized - clear token and redirect to login
      localStorage.removeItem('firebase_token')
      localStorage.removeItem('admin_user')
      window.location.href = '/login'
      return Promise.reject(error)
    }
    
    if (error.response?.status === 403) {
      // Forbidden - user is not admin
      toast.error('Access denied. You are not authorized to use the admin console.')
      return Promise.reject(error)
    }
    
    if (error.response?.status === 404) {
      toast.error('Resource not found')
      return Promise.reject(error)
    }
    
    if (error.response?.status >= 500) {
      toast.error('Server error. Please try again later.')
      return Promise.reject(error)
    }
    
    // Show error message from server
    const errorMessage = error.response?.data?.errorMessage || error.message || 'An error occurred'
    toast.error(errorMessage)
    
    return Promise.reject(error)
  }
)

// API service class
export class AdminApiService {
  // User management
  static async getUsers(filters: Record<string, string | number | boolean> | string = {}) {
    const params = new URLSearchParams()
    if (typeof filters === 'string') {
      if (filters) {
        new URLSearchParams(filters).forEach((value, key) => {
          params.append(key, value)
        })
      }
    } else {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, String(value))
        }
      })
    }
    
    const response = await apiClient.get(`/admin/users?${params.toString()}`)
    return response.data
  }

  static async getUser(firebaseUid: string) {
    const response = await apiClient.get(`/admin/users/${firebaseUid}`)
    return response.data
  }

  static async updateUser(firebaseUid: string, data: any) {
    const response = await apiClient.put(`/admin/users/${firebaseUid}`, data)
    return response.data
  }

  static async deleteUser(firebaseUid: string) {
    const response = await apiClient.delete(`/admin/users/${firebaseUid}`)
    return response.data
  }

  static async exportUsers(filters: Record<string, string | number | boolean> | string = {}) {
    const params = new URLSearchParams()
    if (typeof filters === 'string') {
      if (filters) {
        new URLSearchParams(filters).forEach((value, key) => {
          params.append(key, value)
        })
      }
    } else {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, String(value))
        }
      })
    }
    
    const response = await apiClient.get(`/admin/users/export?${params.toString()}`, {
      responseType: 'blob'
    })
    return response.data
  }

  // Subscription management
  static async updateSubscription(firebaseUid: string, data: any) {
    const response = await apiClient.post(`/admin/users/${firebaseUid}/subscription`, data)
    return response.data
  }

  static async getSubscriptionHistory(firebaseUid: string) {
    const response = await apiClient.get(`/admin/users/${firebaseUid}/subscription-history`)
    return response.data
  }

  static async changeUserTier(firebaseUid: string, data: any) {
    const response = await apiClient.put(`/admin/users/${firebaseUid}/tier`, data)
    return response.data
  }

  static async grantPremiumAccess(firebaseUid: string) {
    const response = await apiClient.post(`/admin/users/${firebaseUid}/premium`, { action: 'grant' })
    return response.data
  }

  static async removePremiumAccess(firebaseUid: string) {
    const response = await apiClient.post(`/admin/users/${firebaseUid}/premium`, { action: 'remove' })
    return response.data
  }

  // Legacy methods for backward compatibility
  static async updateTier(firebaseUid: string, data: any) {
    return this.changeUserTier(firebaseUid, data)
  }

  static async updatePremium(firebaseUid: string) {
    return this.grantPremiumAccess(firebaseUid)
  }

  // Promocode management
  static async getPromoCodes(filters: any = {}) {
    const params = new URLSearchParams()
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, String(value))
      }
    })
    
    const response = await apiClient.get(`/admin/promocodes?${params.toString()}`)
    return response.data
  }

  static async createPromoCode(data: any) {
    const response = await apiClient.post('/admin/promocodes', data)
    return response.data
  }

  static async getPromoCodeUsage(code: string) {
    const response = await apiClient.get(`/admin/promocodes/${code}/usage`)
    return response.data
  }

  static async deletePromoCode(code: string) {
    const response = await apiClient.delete(`/admin/promocodes/${code}`)
    return response.data
  }

  static async exportPromoCodes(filters: any = {}) {
    const params = new URLSearchParams()
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, String(value))
      }
    })
    
    const response = await apiClient.get(`/admin/promocodes/export?${params.toString()}`, {
      responseType: 'blob'
    })
    return response.data
  }

  // Analytics
  static async getUTMAnalytics(filters: any = {}) {
    const params = new URLSearchParams()
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, String(value))
      }
    })
    
    const response = await apiClient.get(`/admin/analytics/utm?${params.toString()}`)
    return response.data
  }

  static async getUserAnalytics(days: number = 30) {
    const response = await apiClient.get(`/admin/analytics/users?days=${days}`)
    return response.data
  }

  static async getDashboardStats() {
    const response = await apiClient.get('/admin/dashboard/stats')
    return response.data
  }

  // Utility methods
  static async downloadCSV(blob: Blob, filename: string) {
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  }
}

export { apiClient }
export default apiClient
